import { memo, useMemo } from 'react'
import { FieldMetaState, useField, UseFieldConfig } from 'react-final-form'
import { INumberInputProps, NumberInput } from 'shared/components/NumberInput'
import { checkFieldError } from 'shared/helpers/form'
import { isNumber } from 'shared/helpers/number'

type TValue = string | number | null | undefined

interface IProps<InputProps extends {}>
  extends Omit<INumberInputProps<InputProps>, 'onChange' | 'onFocus' | 'onBlur' | 'error' | 'value'> {
  name: string
  calculateError?: (meta: FieldMetaState<TValue | undefined>) => boolean | string | undefined
  /** явная ошибка инпута — переопределяет calculateError */
  error?: boolean | string
  /** дополнительный обработчик blur (вызывается после input.onBlur) */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** дополнительный обработчик focus (вызывается после input.onFocus) */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  parse?: UseFieldConfig<TValue>['parse']
  validate?: UseFieldConfig<TValue>['validate']
  validateWarning?: (value: string | number, meta: FieldMetaState<TValue>) => string | undefined
}

const FieldNumberNoMemo = <InputProps extends {}>({
  name,
  validate,
  validateWarning,
  parse = (value: TValue) => (isNumber(value) ? Number(String(value)) : undefined),
  size = 'nm',
  calculateError = checkFieldError,
  error,
  onBlur,
  onFocus,
  ...props
}: IProps<InputProps>) => {
  const { input, meta } = useField(name, {
    validate,
    parse,
    format: (value) => (isNumber(value) ? String(value) : '')
  })
  const value = input.value === undefined || input.value === null ? '' : input.value
  const warning = useMemo(() => validateWarning?.(value, meta), [value, meta, validateWarning])

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    input.onBlur(event)
    onBlur?.(event)
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    input.onFocus(event)
    onFocus?.(event)
  }

  return (
    <NumberInput
      {...props}
      error={error ?? calculateError(meta)}
      name={name}
      onBlur={handleBlur}
      onChange={input.onChange}
      onFocus={handleFocus}
      size={size}
      value={value}
      warning={warning}
    />
  )
}

export const FieldNumber = memo(FieldNumberNoMemo) as typeof FieldNumberNoMemo
